import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SpaIcon from '@mui/icons-material/Spa';
import { useAuth } from '../contexts/AuthContext';
import { AuthError, signInAdmin } from '../services/AuthService';
import { isAdminMfaEnrolled, verifyAdminMfaCode } from '../services/MfaService';
import { brand } from '../theme';

type LoginStep = 'credentials' | 'mfa';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    isAdmin,
    mfaEnrolled,
    mfaVerified,
    refreshAuthState,
    completeMfaVerification,
  } = useAuth();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingUid, setPendingUid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTarget = (location.state as { from?: string } | null)?.from ?? '/';

  React.useEffect(() => {
    if (user && isAdmin && mfaVerified) {
      navigate(redirectTarget, { replace: true });
      return;
    }

    if (user && isAdmin && !mfaEnrolled) {
      navigate('/mfa-setup', { replace: true });
      return;
    }

    if (user && isAdmin && mfaEnrolled && !mfaVerified) {
      setPendingUid(user.uid);
      setStep('mfa');
    }
  }, [user, isAdmin, mfaEnrolled, mfaVerified, navigate, redirectTarget]);

  const handleCredentialsSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const signedInUser = await signInAdmin(email.trim(), password);
      await refreshAuthState();

      const enrolled = await isAdminMfaEnrolled(signedInUser.uid);
      if (!enrolled) {
        navigate('/mfa-setup', { replace: true });
        return;
      }

      setPendingUid(signedInUser.uid);
      setStep('mfa');
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Giriş başarısız.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const uid = pendingUid ?? user?.uid;

    if (!uid) {
      setError('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
      setStep('credentials');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const valid = await verifyAdminMfaCode(uid, verificationCode.trim());
      if (!valid) {
        setError('Doğrulama kodu hatalı.');
        return;
      }

      completeMfaVerification();
      navigate(redirectTarget, { replace: true });
    } catch {
      setError('Doğrulama başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: brand.slate900,
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.35)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(brand.emerald, 0.15),
                color: brand.emerald,
                mb: 2,
              }}
            >
              {step === 'credentials' ? <SpaIcon /> : <LockOutlinedIcon />}
            </Box>
            <Typography variant="h5" fontWeight={700} textAlign="center">
              {step === 'credentials' ? 'Admin Girişi' : 'İki Faktörlü Doğrulama'}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              {step === 'credentials'
                ? 'Zamanında Tüket yönetim paneli'
                : 'Authenticator uygulamanızdaki 6 haneli kodu girin'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === 'credentials' ? (
            <Box component="form" onSubmit={handleCredentialsSubmit}>
              <TextField
                fullWidth
                label="E-posta"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Şifre"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {submitting ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleMfaSubmit}>
              <TextField
                fullWidth
                label="Doğrulama Kodu"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting || verificationCode.length !== 6}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {submitting ? 'Doğrulanıyor...' : 'Doğrula ve Giriş Yap'}
              </Button>
              <Button
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
                onClick={() => {
                  setStep('credentials');
                  setVerificationCode('');
                  setPendingUid(null);
                }}
              >
                Geri dön
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
